import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { translateApiError } from '../../utils/errorMessages'
import api from '../../services/api'
import EntityHistory from '../../components/EntityHistory'
import ConfirmModal from '../../components/ConfirmModal'
import Icon from '../../components/Icon'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Button, Input, Select, FormGroup } from '../../components/ui'
import './Admin.css'

const formatPercentage = (value) => {
  if (value == null) return '-'
  return `${Number(value).toFixed(2)}%`
}

const formatEditablePercentage = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : ''
}

const subTeamKey = (value) => String(value || '').trim().toLocaleLowerCase('ru')

function CustomTeamPercentages() {
  const [searchParams] = useSearchParams()
  const budgetIdFromUrl = searchParams.get('budget_id') || ''
  const stageIdFromUrl = searchParams.get('stage_id') || ''
  const contractIdFromUrl = searchParams.get('contract_id') || ''
  const shouldCreateFromUrl = searchParams.get('create') === '1'
  const [budgetFilter, setBudgetFilter] = useState(null)
  const [budgetFilterLoading, setBudgetFilterLoading] = useState(Boolean(budgetIdFromUrl))
  const [grouped, setGrouped] = useState([])
  const [contracts, setContracts] = useState([])
  const [stages, setStages] = useState([])
  const [employees, setEmployees] = useState([])
  const [formEmployees, setFormEmployees] = useState(null)
  const [teamOptions, setTeamOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [distributionFilter, setDistributionFilter] = useState('all')
  const [modal, setModal] = useState(null) // { type: 'add' | 'edit', record?: { id, contract_id, contract_number, stage_id, team, employee_name, percentage } }
  const [form, setForm] = useState({
    contract_id: '',
    stage_id: '',
    team: '',
    team_percentages: {},
    team_roles: {}
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const createIntentHandled = useRef(false)
  const resolvedStageIdFromUrl = budgetFilter?.stage_id || stageIdFromUrl
  const resolvedContractIdFromUrl = budgetFilter?.contract_id || contractIdFromUrl

  const stagesFiltered = form.contract_id
    ? stages.filter((s) => String(s.contract_id) === String(form.contract_id))
    : stages
  const uniqueTeamOptions = teamOptions.reduce((unique, option) => {
    const name = String(option?.team_name || option?.value || option?.label || '').trim()
    if (!name || unique.some((item) => item.name.toLocaleLowerCase('ru') === name.toLocaleLowerCase('ru'))) {
      return unique
    }
    unique.push({ name, label: option.label || name })
    return unique
  }, [])

  useEffect(() => {
    fetchPercentages()
    fetchContracts()
    fetchStages()
    fetchEmployees()
    fetchTeamOptions()
  }, [])

  useEffect(() => {
    let alive = true

    const fetchBudgetFilter = async () => {
      if (!budgetIdFromUrl) {
        setBudgetFilter(null)
        setBudgetFilterLoading(false)
        return
      }

      try {
        setBudgetFilterLoading(true)
        const res = await api.get(`/company/budgets/${budgetIdFromUrl}`)
        const budget = res.data?.budget || {}
        if (!alive) return
        setBudgetFilter({
          stage_id: budget.legacy_stage_id || budget.stage_id || '',
          contract_id: budget.contract_id || ''
        })
      } catch (e) {
        console.error('Error resolving budget filter:', e)
        if (alive) setBudgetFilter({ stage_id: stageIdFromUrl, contract_id: contractIdFromUrl })
      } finally {
        if (alive) setBudgetFilterLoading(false)
      }
    }

    fetchBudgetFilter()
    return () => {
      alive = false
    }
  }, [budgetIdFromUrl, contractIdFromUrl, stageIdFromUrl])

  const fetchContracts = async () => {
    try {
      const res = await api.get('/admin/budget/contracts')
      if (res.data?.contracts) setContracts(res.data.contracts)
    } catch (e) {
      console.error('Error fetching contracts:', e)
    }
  }

  const fetchStages = async () => {
    try {
      const res = await api.get('/admin/budget/stages')
      if (res.data?.stages) setStages(res.data.stages)
    } catch (e) {
      console.error('Error fetching stages:', e)
    }
  }

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/admin/budget/employees-simple')
      if (res.data?.employees) setEmployees(res.data.employees)
    } catch (e) {
      console.error('Error fetching employees:', e)
    }
  }

  const fetchStageEmployees = async (stageId) => {
    if (!stageId) return employees
    const res = await api.get(`/admin/budget/employees-simple?stage_id=${encodeURIComponent(stageId)}`)
    return res.data?.employees || []
  }

  const fetchTeamOptions = async () => {
    try {
      const res = await api.get('/admin/budget/team-options')
      if (res.data?.team_options) setTeamOptions(res.data.team_options)
    } catch (e) {
      console.error('Error fetching team options:', e)
    }
  }

  const fetchPercentages = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/budget/custom-team-percentages')
      if (response.data) {
        if (response.data.grouped) setGrouped(response.data.grouped)
        setTotal(response.data.total || 0)
      }
    } catch (err) {
      console.error('Error fetching custom team percentages:', err)
      alert('Ошибка загрузки данных: ' + translateApiError(err.response?.data?.error || err.message))
    } finally {
      setLoading(false)
    }
  }

  const flattenedRows = grouped.flatMap((stageGroup) =>
    (stageGroup.teams || []).flatMap((teamGroup) =>
      (teamGroup.employees || []).map((emp) => ({
        ...emp,
        stage_id: stageGroup.stage_id,
        team: teamGroup.team
      }))
    )
  )

  const distributionRows = grouped.flatMap((stageGroup) =>
    (stageGroup.teams || []).map((teamGroup) => {
      const employeesInTeam = teamGroup.employees || []
      const firstEmployee = employeesInTeam[0] || {}
      const customTotalPercentage = employeesInTeam.reduce((sum, employee) => sum + (Number(employee.percentage) || 0), 0)
      const hasEffectiveTotal = teamGroup.effective_total_percentage !== null
        && teamGroup.effective_total_percentage !== undefined
        && Number.isFinite(Number(teamGroup.effective_total_percentage))
      const hasEffectiveDifference = teamGroup.effective_difference !== null
        && teamGroup.effective_difference !== undefined
        && Number.isFinite(Number(teamGroup.effective_difference))
      const totalPercentage = hasEffectiveTotal
        ? Number(teamGroup.effective_total_percentage)
        : customTotalPercentage
      const difference = hasEffectiveDifference
        ? Number(teamGroup.effective_difference)
        : totalPercentage - 100
      return {
        contract_id: firstEmployee.contract_id || stageGroup.contract_id,
        contract_number: firstEmployee.contract_number || stageGroup.contract_number || '—',
        stage_id: stageGroup.stage_id,
        team: teamGroup.team,
        employee_count: Number(teamGroup.effective_employee_count) || employeesInTeam.length,
        total_percentage: Number(totalPercentage.toFixed(2)),
        difference: Number(difference.toFixed(2)),
        record: {
          ...firstEmployee,
          contract_id: firstEmployee.contract_id || stageGroup.contract_id,
          stage_id: stageGroup.stage_id,
          team: teamGroup.team,
        },
        records: employeesInTeam,
      }
    })
  )

  const filteredRows = distributionRows.filter((row) => {
    if (resolvedContractIdFromUrl && String(row.contract_id) !== String(resolvedContractIdFromUrl)) return false
    if (resolvedStageIdFromUrl && String(row.stage_id) !== String(resolvedStageIdFromUrl)) return false
    if (distributionFilter === 'issue' && Math.abs(row.difference) < 0.01) return false
    if (distributionFilter === 'ok' && Math.abs(row.difference) >= 0.01) return false
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase().trim()
    return (
      (row.contract_number || '').toLowerCase().includes(q) ||
      (row.stage_id || '').toLowerCase().includes(q) ||
      (row.team || '').toLowerCase().includes(q)
    )
  })
  const hasActiveFilter = Boolean(searchQuery.trim() || distributionFilter !== 'all' || resolvedContractIdFromUrl || resolvedStageIdFromUrl)

  const getTeamMembers = (teamName, sourceEmployees = (formEmployees ?? employees)) => {
    if (!teamName) return []
    const normalizedTeam = String(teamName).trim()
    return sourceEmployees.map((employee) => {
      const assignments = Array.isArray(employee.team_assignments) ? employee.team_assignments : []
      const assignment = assignments.find((item) => String(item?.team || item?.team_name || '').trim() === normalizedTeam)
      const belongsByFallback = !assignment && (employee.teams || []).some((team) => String(team).trim() === normalizedTeam)
      if (!assignment && !belongsByFallback) return null
      return {
        id: String(employee.id),
        employee_name: employee.full_name || employee.id,
        base_percentage: Number(assignment?.base_percentage ?? employee.base_percentage ?? 0),
        sub_team: String(assignment?.sub_team || '').trim()
      }
    }).filter(Boolean).sort((a, b) => a.employee_name.localeCompare(b.employee_name, 'ru'))
  }

  const makeTeamPercentageDraft = ({ contract_id, stage_id, team }, sourceEmployees = employees) => {
    const existingByEmployeeId = new Map()
    flattenedRows
      .filter((row) => (
        String(row.contract_id || '') === String(contract_id || '')
        && String(row.stage_id || '') === String(stage_id || '')
        && String(row.team || '') === String(team || '')
      ))
      .forEach((row) => {
        const employeeId = String(row.employee_id || '')
        if (!employeeId) return
        const rows = existingByEmployeeId.get(employeeId) || []
        rows.push({ sub_team: String(row.sub_team || '').trim(), percentage: row.percentage })
        existingByEmployeeId.set(employeeId, rows)
      })
    return getTeamMembers(team, sourceEmployees).reduce((draft, member) => {
      const existingRoles = existingByEmployeeId.get(member.id) || []
      const roles = existingRoles.length
        ? existingRoles
        : [{ sub_team: member.sub_team, percentage: member.base_percentage }]
      draft.team_percentages[member.id] = formatEditablePercentage(roles[0].percentage)
      draft.team_roles[member.id] = roles.map((role) => ({
        sub_team: role.sub_team || member.sub_team || '',
        percentage: formatEditablePercentage(role.percentage)
      }))
      return draft
    }, { team_percentages: {}, team_roles: {} })
  }

  const teamMembers = getTeamMembers(form.team)
  const teamHasSubTeams = teamMembers.some((member) => member.sub_team)
  const availableSubTeams = [...new Set(teamMembers.map((member) => member.sub_team).filter(Boolean))]
  const teamRoles = teamMembers.flatMap((member) => {
    const roles = form.team_roles?.[member.id] || [{
      sub_team: member.sub_team,
      percentage: form.team_percentages?.[member.id] ?? formatEditablePercentage(member.base_percentage)
    }]
    return roles.map((role, roleIndex) => ({ ...member, ...role, roleIndex }))
  })
  const teamPercentagesAreValid = teamRoles.length > 0 && teamRoles.every((role) => {
    const value = Number(role.percentage)
    return role.percentage !== '' && Number.isFinite(value) && value >= 0 && value <= 100
      && (!teamHasSubTeams || Boolean(role.sub_team))
  })
  const teamTotal = teamRoles.reduce((sum, role) => sum + (Number(role.percentage) || 0), 0)
  const isTeamTotalValid = teamPercentagesAreValid && Math.abs(teamTotal - 100) < 0.01
  const teamDifference = Math.abs(100 - teamTotal)

  const formatStageLabel = (stage) => {
    const contractNumber = (stage?.contract_number || '').trim()
    const stageType = (stage?.distribution_type || stage?.type || '').trim()
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(stageType)
    const resolvedType = stageType && !looksLikeUuid
      ? stageType
      : String(stage?.id || '').split(/ - | — /).pop()?.trim() || ''
    if (contractNumber && resolvedType) return `${contractNumber} - ${resolvedType}`
    return resolvedType || contractNumber || String(stage?.id || '-')
  }

  const stagesLabelById = stages.reduce((acc, stage) => {
    acc[String(stage.id)] = formatStageLabel(stage)
    return acc
  }, {})

  const openAdd = () => {
    setFormEmployees(null)
    setForm({
      contract_id: resolvedContractIdFromUrl,
      stage_id: resolvedStageIdFromUrl,
      team: '',
      team_percentages: {},
      team_roles: {}
    })
    setError(null)
    setModal({ type: 'add' })
  }

  usePageHeader({
    renderActions: () => (
      <Button variant="primary" onClick={openAdd} aria-label="Добавить проценты">
        Добавить проценты
      </Button>
    ),
  }, [resolvedContractIdFromUrl, resolvedStageIdFromUrl])

  useEffect(() => {
    if (!shouldCreateFromUrl || createIntentHandled.current || loading || budgetFilterLoading || modal) return
    createIntentHandled.current = true
    openAdd()
  }, [shouldCreateFromUrl, loading, budgetFilterLoading, modal, resolvedContractIdFromUrl, resolvedStageIdFromUrl])

  const openEdit = async (record) => {
    const next = {
      contract_id: record.contract_id ?? '',
      stage_id: record.stage_id ?? '',
      team: record.team ?? '',
    }
    let stageEmployees = employees
    try {
      stageEmployees = await fetchStageEmployees(next.stage_id)
      setFormEmployees(stageEmployees)
    } catch (e) {
      console.error('Error fetching stage employees:', e)
    }
    setForm({ ...next, ...makeTeamPercentageDraft(next, stageEmployees) })
    setError(null)
    setModal({ type: 'edit', record })
  }

  const handleContractChange = (value) => {
    setForm((f) => {
      const next = { ...f, contract_id: value }
      const stageStillValid = !value || stages.some((s) => String(s.contract_id) === String(value) && String(s.id) === String(f.stage_id))
      if (!stageStillValid) {
        next.stage_id = ''
        setFormEmployees(null)
      }
      const draft = next.team ? makeTeamPercentageDraft(next) : { team_percentages: {}, team_roles: {} }
      next.team_percentages = draft.team_percentages
      next.team_roles = draft.team_roles
      return next
    })
  }

  const handleStageChange = async (value) => {
    const stage = stages.find((item) => String(item.id) === String(value))
    let stageEmployees = employees
    try {
      stageEmployees = await fetchStageEmployees(value)
      setFormEmployees(value ? stageEmployees : null)
    } catch (e) {
      console.error('Error fetching stage employees:', e)
    }
    setForm((f) => {
      const next = { ...f, stage_id: value, contract_id: stage?.contract_id ?? f.contract_id }
      const draft = next.team ? makeTeamPercentageDraft(next, stageEmployees) : { team_percentages: {}, team_roles: {} }
      next.team_percentages = draft.team_percentages
      next.team_roles = draft.team_roles
      return next
    })
  }

  const handleTeamChange = (value) => {
    setForm((f) => {
      const next = { ...f, team: value }
      const draft = value ? makeTeamPercentageDraft(next) : { team_percentages: {}, team_roles: {} }
      next.team_percentages = draft.team_percentages
      next.team_roles = draft.team_roles
      return next
    })
  }

  const handleTeamPercentageChange = (employeeId, roleIndex, value) => {
    setForm((f) => ({
      ...f,
      team_percentages: { ...f.team_percentages, [employeeId]: roleIndex === 0 ? value : f.team_percentages?.[employeeId] },
      team_roles: {
        ...f.team_roles,
        [employeeId]: (f.team_roles?.[employeeId] || []).map((role, index) => index === roleIndex ? { ...role, percentage: value } : role)
      }
    }))
  }

  const handleRoleSubTeamChange = (employeeId, roleIndex, subTeam) => {
    setForm((f) => ({
      ...f,
      team_roles: {
        ...f.team_roles,
        [employeeId]: (f.team_roles?.[employeeId] || []).map((role, index) => index === roleIndex ? { ...role, sub_team: subTeam } : role)
      }
    }))
  }

  const addRole = (member) => {
    setForm((f) => {
      const currentRoles = f.team_roles?.[member.id] || [{
        sub_team: member.sub_team || '',
        percentage: f.team_percentages?.[member.id] ?? formatEditablePercentage(member.base_percentage)
      }]
      const usedSubTeams = new Set(currentRoles.map((role) => subTeamKey(role.sub_team)).filter(Boolean))
      const nextSubTeam = availableSubTeams.find((subTeam) => !usedSubTeams.has(subTeamKey(subTeam))) || ''

      return {
        ...f,
        team_roles: {
          ...f.team_roles,
          [member.id]: [...currentRoles, { sub_team: nextSubTeam, percentage: '' }]
        }
      }
    })
  }

  const removeRole = (employeeId, roleIndex) => {
    setForm((f) => ({
      ...f,
      team_roles: {
        ...f.team_roles,
        [employeeId]: (f.team_roles?.[employeeId] || []).filter((_, index) => index !== roleIndex)
      }
    }))
  }

  const handleSave = async () => {
    const contract_id = (form.contract_id || '').trim() || null
    const stage_id = (form.stage_id || '').trim() || null
    const team = (form.team || '').trim() || null
    if (!stage_id) {
      setError('Укажите стадию')
      return
    }
    if (!team) {
      setError('Укажите команду, чтобы увидеть распределение')
      return
    }
    if (!isTeamTotalValid) {
      setError('Итого по команде должно быть ровно 100%')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        contract_id: contract_id || undefined,
        stage_id,
        team,
        percentages: teamRoles.map((role) => ({
          employee_id: role.id,
          ...(teamHasSubTeams ? { sub_team: role.sub_team } : {}),
          percentage: Number(role.percentage)
        }))
      }
      await api.put('/admin/budget/custom-team-percentages/team', payload)
      setModal(null)
      await fetchPercentages()
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setDeleteError(null)
      await api.delete('/admin/budget/custom-team-percentages/team', {
        data: {
          contract_id: deleteTarget.contract_id,
          stage_id: deleteTarget.stage_id,
          team: deleteTarget.team,
        },
      })
      setDeleteTarget(null)
      setDeleteError(null)
      await fetchPercentages()
    } catch (err) {
      setDeleteError(translateApiError(err.response?.data?.error || err.message))
    }
  }

  const resetFilters = () => {
    setSearchQuery('')
    setDistributionFilter('all')
  }

  if (loading || budgetFilterLoading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="admin-page">
      <div className="section-card">
        <div className="custom-team-percentages-filters">
          <FormGroup label="Поиск" className="custom-team-percentages-filter-search custom-team-percentages-filter-search--double">
            <Input
              type="search"
              variant="search"
              iconName="search"
              className="custom-team-percentages-search-input--wide"
              aria-label="Поиск распределений"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Договор, стадия или команда"
            />
          </FormGroup>
          <FormGroup label="Показывать">
            <Select aria-label="Показывать распределения" value={distributionFilter} onChange={(e) => setDistributionFilter(e.target.value)}>
              <option value="all">Все распределения</option>
              <option value="issue">Только с расхождением</option>
              <option value="ok">Только 100%</option>
            </Select>
          </FormGroup>
          <Button variant="secondary" onClick={resetFilters} disabled={!hasActiveFilter}>
            Сбросить
          </Button>
        </div>
        <div className="custom-team-percentages-results-summary">
          <p className="admin-subtitle">
            {hasActiveFilter
              ? `Найдено ${filteredRows.length} из ${distributionRows.length} распределений`
              : `Всего ${distributionRows.length} ${distributionRows.length === 1 ? 'распределение' : distributionRows.length < 5 ? 'распределения' : 'распределений'}`}
          </p>
          <div className="custom-team-percentages-legend" aria-label="Обозначения статусов">
            <span className="is-warning">Не хватает</span>
            <span className="is-error">Превышение</span>
            <span className="is-success">100%</span>
          </div>
        </div>
        {grouped.length === 0 ? (
          <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>
            Нет данных. Добавьте первую запись.
          </p>
        ) : filteredRows.length === 0 ? (
          <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>Ничего не найдено</p>
        ) : (
          <div className="admin-table-frame">
            <div className="table-responsive">
              <table className="ui-table data-table">
                <thead>
                  <tr>
                    <th>Договор</th>
                    <th>Стадия</th>
                    <th>Команда</th>
                    <th>Итог команды</th>
                    <th>Отклонение</th>
                    <th style={{ width: '88px' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={`${row.contract_id || ''}-${row.stage_id}-${row.team}`}>
                      <td>{row.contract_number || '—'}</td>
                      <td>{stagesLabelById[String(row.stage_id)] || row.stage_id || '—'}</td>
                      <td>{row.team || '—'}</td>
                      <td>
                        <div className="custom-team-percentages-total-cell">
                          <span className="amount-mono">{formatPercentage(row.total_percentage)}</span>
                          <span className={`custom-team-percentages-progress ${row.difference > 0.01 ? 'is-over' : row.difference < -0.01 ? 'is-under' : 'is-valid'}`} aria-hidden>
                            <span style={{ width: `${Math.min(row.total_percentage, 100)}%` }} />
                          </span>
                        </div>
                      </td>
                      <td>
                        {Math.abs(row.difference) < 0.01 ? (
                          <span className="custom-team-percentages-status is-valid">Распределено</span>
                        ) : row.difference < 0 ? (
                          <span className="custom-team-percentages-status is-under">Не хватает {formatPercentage(Math.abs(row.difference))}</span>
                        ) : (
                          <span className="custom-team-percentages-status is-over">Превышение {formatPercentage(row.difference)}</span>
                        )}
                      </td>
                      <td>
                        <div className="custom-team-percentages-actions">
                        <Button
                          variant="ghost"
                          className="icon-button"
                          onClick={() => openEdit(row.record)}
                          aria-label={`Редактировать: ${row.team}`}
                          title="Редактировать"
                        >
                          <Icon name="edit" size={16} alt="" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          className="icon-button"
                          onClick={() =>
                            setDeleteTarget({
                              contract_id: row.contract_id || undefined,
                              stage_id: row.stage_id,
                              team: row.team,
                            })
                          }
                          title="Удалить распределение команды"
                          aria-label={`Удалить распределение команды: ${row.team}`}
                        >
                          <Icon name="trash" size={16} alt="" aria-hidden="true" />
                        </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <EntityHistory entityType="custom_team_percentages" title="История изменений (уникальные проценты)" />

      {modal && (modal.type === 'add' || modal.type === 'edit') && (
        <div className="modal-overlay" onClick={() => !saving && setModal(null)}>
          <div className="modal-content custom-team-percentages-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal.type === 'add' ? 'Добавить запись' : 'Изменить распределение'}</h3>
              <button className="modal-close" type="button" onClick={() => !saving && setModal(null)} aria-label="Закрыть">
                <Icon name="close" size={20} />
              </button>
            </div>
            {error && (
              <p className="modal-error" style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-md)' }}>
                {error}
              </p>
            )}
            <div className="custom-team-percentages-modal-grid">
              <div className="custom-team-percentages-context">
                <FormGroup label="Договор">
                  <Select value={form.contract_id} onChange={(e) => handleContractChange(e.target.value)}>
                    <option value="">— не выбран —</option>
                    {contracts.map((c) => (
                      <option key={c.id} value={c.id}>{c.number || c.id}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup label="Стадия">
                  <Select value={form.stage_id} onChange={(e) => handleStageChange(e.target.value)}>
                    <option value="">— не выбрана —</option>
                    {stagesFiltered.map((s) => (
                      <option key={s.id} value={s.id}>{formatStageLabel(s)}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup label="Команда">
                  <Select value={form.team} onChange={(e) => handleTeamChange(e.target.value)}>
                    <option value="">— не выбрана —</option>
                    {uniqueTeamOptions.map((option) => (
                      <option key={option.name} value={option.name}>{option.label}</option>
                    ))}
                  </Select>
                </FormGroup>
                <p className="custom-team-percentages-context-hint">
                  После выбора команды задайте уникальные проценты для всех её сотрудников.
                </p>
              </div>

              <div className="custom-team-percentages-impact" aria-live="polite">
                {form.team ? (
                  <>
                    <div className="custom-team-percentages-impact-header">
                      <div>
                        <h4>Влияние на команду</h4>
                        <span className="custom-team-percentages-team-name">{form.team}</span>
                      </div>
                      <div className={`custom-team-percentages-total ${isTeamTotalValid ? 'is-valid' : 'is-invalid'}`}>
                        <strong>Итого: {Number(teamTotal.toFixed(2))}%</strong>
                        <span>{isTeamTotalValid ? 'Распределение сходится' : `${teamTotal < 100 ? 'Не хватает' : 'Превышение'} ${Number(teamDifference.toFixed(2))} п.п.`}</span>
                      </div>
                    </div>
                    <p className="custom-team-percentages-notice">Сумма всех ролей команды должна быть ровно 100%.</p>
                    {teamMembers.length ? (
                      <div className="custom-team-percentages-table-wrap">
                        <table className="ui-table custom-team-percentages-table">
                          <thead>
                            <tr>
                              <th>Сотрудник</th>
                              <th>Базовый</th>
                              <th>Подкоманда</th>
                              <th>Уникальный</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamMembers.map((member) => {
                              const roles = teamRoles.filter((role) => role.id === member.id)
                              const usedSubTeams = new Set(roles.map((role) => subTeamKey(role.sub_team)).filter(Boolean))
                              const canAddRole = availableSubTeams.some((subTeam) => !usedSubTeams.has(subTeamKey(subTeam)))
                              const roleOptions = (role) => availableSubTeams.filter((subTeam) => (
                                subTeamKey(subTeam) === subTeamKey(role.sub_team)
                                || !roles.some((otherRole) => (
                                  otherRole.roleIndex !== role.roleIndex
                                  && subTeamKey(otherRole.sub_team) === subTeamKey(subTeam)
                                ))
                              ))

                              return (
                                <React.Fragment key={member.id}>
                                  {roles.map((role) => {
                                    const isPrimaryRole = role.roleIndex === 0
                                    return (
                                      <tr className={isPrimaryRole ? 'custom-team-percentages-member-row' : 'custom-team-percentages-member-row custom-team-percentages-member-row--additional'} key={`${member.id}-${role.roleIndex}`}>
                                        <td>
                                          {isPrimaryRole
                                            ? <strong className="custom-team-percentages-member-name">{member.employee_name}</strong>
                                            : <span className="custom-team-percentages-role-continuation" aria-label={`Дополнительная роль: ${member.employee_name}`} />}
                                        </td>
                                        <td>{isPrimaryRole ? formatPercentage(member.base_percentage) : '0.00%'}</td>
                                        <td>
                                          {teamHasSubTeams ? (
                                            <div className="custom-team-percentages-subteam-control">
                                              <Select
                                                aria-label={`Подкоманда: ${member.employee_name}, роль ${role.roleIndex + 1}`}
                                                value={role.sub_team}
                                                onChange={(e) => handleRoleSubTeamChange(member.id, role.roleIndex, e.target.value)}
                                              >
                                                <option value="">— выберите —</option>
                                                {roleOptions(role).map((subTeam) => <option key={subTeam} value={subTeam}>{subTeam}</option>)}
                                              </Select>
                                              {isPrimaryRole && (
                                                <Button
                                                  variant="ghost"
                                                  type="button"
                                                  className="custom-team-percentages-add-role"
                                                  onClick={() => addRole(member)}
                                                  aria-label={`Добавить подкоманду: ${member.employee_name}`}
                                                  disabled={!canAddRole}
                                                  title={canAddRole ? 'Добавить подкоманду' : 'Все подкоманды команды уже выбраны'}
                                                >
                                                  <Icon name="plus" size={15} aria-hidden="true" alt="" />
                                                </Button>
                                              )}
                                            </div>
                                          ) : '—'}
                                        </td>
                                        <td>
                                          <div className="custom-team-percentages-unique-control">
                                            <Input
                                              type="number"
                                              min={0}
                                              max={100}
                                              step={0.01}
                                              aria-label={`Уникальный процент: ${member.employee_name}, роль ${role.roleIndex + 1}`}
                                              value={role.percentage}
                                              onChange={(e) => handleTeamPercentageChange(member.id, role.roleIndex, e.target.value)}
                                            />
                                            {roles.length > 1 && (
                                              <Button
                                                variant="ghost"
                                                type="button"
                                                className="custom-team-percentages-remove-role"
                                                onClick={() => removeRole(member.id, role.roleIndex)}
                                                aria-label={`Удалить роль: ${member.employee_name}, ${role.roleIndex + 1}`}
                                                title="Удалить роль"
                                              >
                                                <Icon name="trash" size={15} aria-hidden="true" alt="" />
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="custom-team-percentages-empty">В выбранной команде нет сотрудников с базовыми процентами.</p>
                    )}
                  </>
                ) : (
                  <div className="custom-team-percentages-placeholder">
                    <strong>Влияние на команду</strong>
                    <span>Выберите команду — здесь появятся все её сотрудники и итог распределения.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => !saving && setModal(null)} disabled={saving}>
                Отмена
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving || !isTeamTotalValid}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Удалить распределение команды?"
          message={`Будут удалены все уникальные проценты команды «${deleteTarget.team}» в выбранных договоре и стадии.`}
          confirmLabel="Удалить"
          onConfirm={handleDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
          error={deleteError}
        />
      )}
    </div>
  )
}

export default CustomTeamPercentages
